import React from 'react';

interface DotsLegendPayloadEntry {
  value?: string;
  color?: string;
  /** Shown after the label. Only callers that build their own payload set it (a series total). */
  trailing?: string;
  // recharts also passes inactive/dataKey etc. — we ignore them.
}

interface DotsLegendProps {
  /** Provided by recharts when this component is used as `<Legend content={...} />`. */
  payload?: DotsLegendPayloadEntry[];
}

/**
 * Custom legend renderer used by Score Trend and Performance Radar charts.
 * Matches the design reference: colored circular dots + label, centered
 * horizontally below the chart, with wrap-friendly spacing.
 *
 * Plug into recharts via:
 *   <Legend content={<DotsLegend />} verticalAlign="bottom" />
 */
export const DotsLegend: React.FC<DotsLegendProps> = ({ payload }) => {
  if (!payload?.length) return null;
  return (
    <ul className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 pt-3 text-[11px]">
      {payload.map(entry => (
        <li key={String(entry.value ?? entry.color)} className="inline-flex items-center gap-1.5">
          <span
            aria-hidden
            className="inline-block h-2 w-2 rounded-full shrink-0"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-foreground">{entry.value}</span>
          {entry.trailing && (
            <span className="tabular-nums text-muted-foreground">{entry.trailing}</span>
          )}
        </li>
      ))}
    </ul>
  );
};
